/**
 * Operatoren der Filterzeile — Port des privaten `Operator`-Enums, der Menüeinträge und der
 * `FilterModeIcons`-Tabelle aus `IwmGridFilterRow.razor`. Texte und Symbolklassen bleiben
 * wörtlich erhalten.
 */

import type { DropDownItem } from '@/components/ui';

import type { FilterOperator } from './types';

export type FilterMode =
  | 'Contains'
  | 'StartsWith'
  | 'EndsWith'
  | 'Equal'
  | 'NotEqual'
  | 'NotContains'
  | 'GreaterThan'
  | 'GreaterThanOrEqual'
  | 'LessThan'
  | 'LessThanOrEqual'
  | 'Between';

/** `FilterModeIcons` — bestimmt das Symbol der Schaltfläche links vom Eingabefeld. */
export const filterModeIcons: Readonly<Record<FilterMode, string>> = {
  Contains: 'e-text-that-contains',
  StartsWith: 'e-arrow-right',
  EndsWith: 'e-arrow-left',
  Equal: 'e-equalto',
  NotEqual: 'e-conditional-formatting-large red',
  NotContains: 'e-text-that-contains red',
  GreaterThan: 'e-greater-than',
  GreaterThanOrEqual: 'e-filter',
  LessThan: 'e-less-than',
  LessThanOrEqual: 'e-filter',
  Between: 'e-filter',
};

/** Menü der Textspalten; Startoperator ist `Contains`. */
export const TEXT_FILTER_ITEMS: readonly DropDownItem[] = [
  { id: 'Contains', text: 'Contains', iconClass: 'e-icons e-text-that-contains' },
  { id: 'StartsWith', text: 'Starts with', iconClass: 'e-icons e-arrow-right' },
  { id: 'EndsWith', text: 'Ends with', iconClass: 'e-icons e-arrow-left' },
  { id: 'Equal', text: 'Equal to', iconClass: 'e-icons e-equalto' },
  { id: 'NotEqual', text: 'Not equal to', iconClass: 'e-icons e-conditional-formatting-large red' },
  { id: 'NotContains', text: 'Not contains', iconClass: 'e-icons e-text-that-contains red' },
];

/** Menü der Zahlenspalten (decimal und int); Startoperator ist `Equal`. */
export const NUMBER_FILTER_ITEMS: readonly DropDownItem[] = [
  { id: 'Equal', text: 'Equal to', iconClass: 'e-icons e-equalto' },
  { id: 'GreaterThan', text: 'Größer als / Greater than', iconClass: 'e-icons e-greater-than' },
  { id: 'LessThan', text: 'Kleiner als / Smaller than', iconClass: 'e-icons e-less-than' },
  {
    id: 'GreaterThanOrEqual',
    text: 'Größer gleich / Greater than or equal',
    iconClass: 'e-icons e-filter',
  },
  {
    id: 'LessThanOrEqual',
    text: 'Kleiner gleich / Lower than or equal',
    iconClass: 'e-icons e-filter',
  },
  { id: 'Between', text: 'Zwischen / Between', iconClass: 'e-icons e-filter' },
  { id: 'NotEqual', text: 'Not equal to', iconClass: 'e-icons e-conditional-formatting-large red' },
];

export function isFilterMode(id: string): id is FilterMode {
  return Object.prototype.hasOwnProperty.call(filterModeIcons, id);
}

/**
 * `FilterModeValue.ToString().ToLower()`. `NotContains` filtert im Altclient als `contains`;
 * die Verneinung steckt im `$$NOT$$`-Präfix des Wertes. `Between` hat keinen eigenen Operator —
 * dieser Modus setzt zwei Prädikate über `setColumnWhere` und liefert hier `null`.
 */
export function toFilterOperator(mode: FilterMode): FilterOperator | null {
  if (mode === 'Between') return null;
  return mode === 'NotContains' ? 'contains' : (mode.toLowerCase() as FilterOperator);
}
